import moment from 'moment';
import { FieldExpression, LuceneExpression, NodeExpression, OperatorExpression, RangeExpression, term } from 'lucene';

import {
	DATE_FORMAT_PATTERN,
	QUARTER_FORMAT_PATTERN,
	WEEK_FORMAT_PATTERN
} from './filter-formats';

type ParsedCondition = Record<string, unknown>;
type ParsedGroup = ParsedCondition[];

const OPERATOR_AND = 'AND';
const OPERATOR_OR = 'OR';

function isNodeExpression(expression: LuceneExpression): expression is NodeExpression {
	return 'operator' in expression
		&& 'left' in expression
		&& 'right' in expression
		&& typeof (<NodeExpression>expression).operator === 'string';
}

function isRangeExpression(expression: LuceneExpression): expression is RangeExpression {
	return 'term_min' in expression
		&& 'term_max' in expression
		&& 'field' in expression;
}

function isFieldExpression(expression: LuceneExpression): expression is FieldExpression {
	return 'term' in expression && 'field' in expression;
}

function isOperatorExpression(expression: LuceneExpression): expression is OperatorExpression {
	return 'start' in expression;
}

function normalizeOperator(operator?: string): string {
	return operator ? operator.toUpperCase() : '';
}

function sanitizeTermValue(raw: string): string {
	let current = raw.trim();
	if (!current) {
		return '';
	}

	while ((current.startsWith('"') && current.endsWith('"')) || (current.startsWith('\'') && current.endsWith('\''))) {
		current = current.slice(1, -1);
	}

	try {
		let result = '';
		let literal = '';
		for (let index = 0; index < current.length;) {
			if (current[index] !== '\\') {
				literal += current[index];
				index += 1;
				continue;
			}
			let end = index;
			while (current[end] === '\\') {
				end += 1;
			}
			const slashCount = end - index;
			const nextCharacter = current[end];
			if (nextCharacter === '*' || nextCharacter === '?') {
				if (literal) {
					result += term.unescape(literal);
					literal = '';
				}
				result += '\\'.repeat(Math.floor(slashCount / 2));
				result += slashCount % 2 === 1 ? `\\${ nextCharacter }` : nextCharacter;
				index = end + 1;
			} else {
				literal += current.slice(index, end);
				index = end;
			}
		}
		return result + term.unescape(literal);
	} catch (error) {
		console.warn('Failed to unescape Lucene term', error);
		return current;
	}
}

function normalizeRangeBound(bound: unknown): string {
	if (bound === undefined || bound === null) {
		return '';
	}
	if (typeof bound !== 'string' && typeof bound !== 'number') {
		return '';
	}
	const value = String(bound).trim();
	if (value === '' || value === '*') {
		return '';
	}
	return sanitizeTermValue(value);
}

function resolveExplicitField(expression: LuceneExpression, inheritedField?: string): string | undefined {
	const { field } = <{ field?: string }>expression;
	if (field && field !== '<implicit>') {
		return field;
	}
	return inheritedField;
}

function resolveFieldName(expression: LuceneExpression, inheritedField?: string): string | undefined {
	if (isFieldExpression(expression) || isRangeExpression(expression)) {
		return expression.field;
	}
	const { field } = <{ field?: string }>expression;
	if (field && field !== '<implicit>') {
		return field;
	}
	return inheritedField;
}

function unwrapExpression(expression?: LuceneExpression): LuceneExpression | undefined {
	if (!expression) {
		return undefined;
	}

	if (isOperatorExpression(expression)) {
		return unwrapExpression(expression.left);
	}

	const candidate = <{ left?: LuceneExpression }>expression;
	if (!isNodeExpression(expression) && candidate.left) {
		return unwrapExpression(candidate.left);
	}

	return expression;
}

function extractNodeValue(expression: LuceneExpression): unknown {
	if (isFieldExpression(expression)) {
		return sanitizeTermValue(String(expression.term));
	}

	if (isRangeExpression(expression)) {
		return {
			from: normalizeRangeBound(expression.term_min),
			to: normalizeRangeBound(expression.term_max)
		};
	}

	const { term: termValue } = <{ term?: unknown }>expression;
	if (typeof termValue === 'string' || typeof termValue === 'number') {
		return sanitizeTermValue(String(termValue));
	}

	const { term_min: minBound, term_max: maxBound, termMin, termMax } = <{
		term_min?: unknown;
		term_max?: unknown;
		termMin?: unknown;
		termMax?: unknown;
	}>expression;
	const min = minBound ?? termMin;
	const max = maxBound ?? termMax;

	if (min !== undefined || max !== undefined) {
		return {
			from: normalizeRangeBound(min),
			to: normalizeRangeBound(max)
		};
	}

	return undefined;
}

function splitByAnd(expression?: LuceneExpression): LuceneExpression[] {
	const target = unwrapExpression(expression);
	if (!target) {
		return [];
	}

	if (isNodeExpression(target) && normalizeOperator(target.operator) === OPERATOR_AND) {
		return [...splitByAnd(target.left), ...splitByAnd(target.right)];
	}

	return [target];
}

function buildAlternatives(expression?: LuceneExpression, inheritedField?: string): ParsedGroup {
	const target = unwrapExpression(expression);
	if (!target) {
		return [];
	}

	if (isNodeExpression(target)) {
		const fieldContext = resolveExplicitField(target, inheritedField);
		const operator = normalizeOperator(target.operator);

		if (operator === OPERATOR_OR) {
			const leftAlternatives = buildAlternatives(target.left, fieldContext);
			const rightAlternatives = buildAlternatives(target.right, fieldContext);
			return [...leftAlternatives, ...rightAlternatives];
		}

		if (operator === OPERATOR_AND) {
			const leftAlternatives = buildAlternatives(target.left, fieldContext);
			const rightAlternatives = buildAlternatives(target.right, fieldContext);

			if (leftAlternatives.length === 0) {
				return rightAlternatives;
			}

			if (rightAlternatives.length === 0) {
				return leftAlternatives;
			}

			const combined: ParsedGroup = [];
			for (const leftCondition of leftAlternatives) {
				for (const rightCondition of rightAlternatives) {
					combined.push({ ...leftCondition, ...rightCondition });
				}
			}
			return combined;
		}

		const hasValue = isFieldExpression(target)
			|| isRangeExpression(target)
			|| (<{ term?: unknown }>target).term !== undefined;

		if (!hasValue) {
			return buildAlternatives(target.left, fieldContext);
		}
	}

	const fieldName = resolveFieldName(target, inheritedField);
	if (!fieldName) {
		return [];
	}

	const value = extractNodeValue(target);
	if (value === undefined) {
		return [];
	}

	return [{ [fieldName]: value }];
}

function parseLuceneGroups(expression: LuceneExpression | undefined): ParsedGroup[] {
	return splitByAnd(expression)
		.map(group => buildAlternatives(group))
		.filter(group => group.length > 0);
}

export function extractConditionsByFields(
	expression: LuceneExpression | undefined,
	fields: string[]
): ParsedCondition[] {
	const fieldSet = new Set(fields);
	const groups = parseLuceneGroups(expression);
	const results: ParsedCondition[] = [];

	for (const group of groups) {
		for (const condition of group) {
			const entries = Object.entries(condition)
				.filter(([field]) => fieldSet.has(field));
			if (entries.length === 0) {
				continue;
			}
			results.push(Object.fromEntries(entries));
		}
	}

	return results;
}

export function collectFieldValues(expression: LuceneExpression | undefined, fields: string[]): Map<string, unknown[]> {
	const results = new Map<string, unknown[]>();
	for (const condition of extractConditionsByFields(expression, fields)) {
		for (const [field, value] of Object.entries(condition)) {
			const existing = results.get(field) ?? [];
			existing.push(value);
			results.set(field, existing);
		}
	}
	return results;
}

export function extractCombinedConditionsByFields(
	expression: LuceneExpression | undefined,
	fields: string[]
): ParsedCondition[] {
	if (!expression) {
		return [];
	}
	const fieldSet = new Set(fields);
	const groups = buildAlternatives(expression);
	const results: ParsedCondition[] = [];

	for (const condition of groups) {
		const entries = Object.entries(condition).filter(([field]) => fieldSet.has(field));
		if (entries.length === 0) {
			continue;
		}
		results.push(Object.fromEntries(entries));
	}

	return results;
}

export function ensureString(value: unknown): string | undefined {
	return typeof value === 'string' ? value : undefined;
}

export function ensureRangeValue(value: unknown): { from: string; to: string } | undefined {
	if (typeof value === 'object' && value !== null) {
		const { from, to } = <{ from?: unknown; to?: unknown }>value;
		return {
			from: ensureString(from) ?? '',
			to: ensureString(to) ?? ''
		};
	}
	return undefined;
}

export function deduplicate<T>(values: T[], identity: (value: T) => string): T[] {
	const seen = new Set<string>();
	const ordered: T[] = [];
	for (const value of values) {
		const key = identity(value);
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		ordered.push(value);
	}
	return ordered;
}

export function parseDocumentDropdownValues(
	expression: LuceneExpression,
	baseField: string,
	translationField: string,
	entityField?: string,
	entityName?: string
): { code?: string; translation?: string; entityName?: string }[] {
	const values = new Map<string, { code?: string; translation?: string; entityName?: string }>();
	const targetFields = entityField ? [baseField, translationField, entityField] : [baseField, translationField];

	for (const condition of extractConditionsByFields(expression, targetFields)) {
		for (const [field, rawValue] of Object.entries(condition)) {
			const text = ensureString(rawValue);
			if (!text) {
				continue;
			}
			const isCodeField = field === baseField || field === entityField;
			const key = `${ isCodeField ? 'code' : 'translation' }:${ text }`;
			const current = values.get(key) ?? { code: undefined, translation: undefined, entityName: undefined };
			if (isCodeField) {
				current.code = text;
				if (field === entityField && entityName) {
					current.entityName = entityName;
				}
			} else {
				current.translation = text;
			}
			values.set(key, current);
		}
	}

	return [...values.values()];
}

export function normalizeDateBoundary(value: string, format: string, adjustUpperBound = false) {
	if (!value) {
		return '';
	}

	const parsed = moment(value, format, true);
	if (!parsed.isValid()) {
		return '';
	}

	if (adjustUpperBound) {
		parsed.subtract(1, 'day');
	}

	return parsed.toDate();
}

export function resolveDateFormat(filterDateType: string) {
	switch (filterDateType) {
	case 'week':
		return WEEK_FORMAT_PATTERN;
	case 'quarter':
		return QUARTER_FORMAT_PATTERN;
	default:
		return DATE_FORMAT_PATTERN;
	}
}

export function shouldAdjustUpperBound(filterDateType: string) {
	return filterDateType !== 'week' && filterDateType !== 'quarter';
}

const ESCAPE_REGEXP = /[$()*+.?[\\\]^{|}]/g;

interface BooleanSelection {
	apiName: string;
	selected: boolean;
	value?: string;
}
type BooleanSelectionRow = BooleanSelection[];
type BooleanSelectionGroups = BooleanSelectionRow[];

interface DocumentLastConnectionSelection {
	apiName: string;
	selected: boolean;
	value: string;
}
type DocumentLastConnectionSelectionRow = DocumentLastConnectionSelection[];

interface DocumentSuggestiveListItem {
	id: string | undefined;
	code: string | undefined;
	entityName?: string;
}

export function parseOrderNumberValue(value: string): { reference?: string; item?: string } {
	const trimmed = value.trim();
	if (!trimmed) {
		return { reference: undefined, item: undefined };
	}
	if (trimmed.includes(' - ')) {
		const [referencePart, itemPart] = trimmed.split(' - ');
		const reference = referencePart.trim();
		const item = itemPart.trim();
		return {
			reference: reference || undefined,
			item: item || undefined
		};
	}
	if (trimmed.length > 6 || /[a-z]/i.test(trimmed)) {
		return { reference: trimmed, item: undefined };
	}
	return { reference: undefined, item: trimmed };
}

export const escapeRegExp = (value: string) => value.replace(ESCAPE_REGEXP, String.raw`\$&`);

export function isNonEmptyString(value: unknown): value is string {
	return typeof value === 'string' && value.trim().length > 0;
}

export function parseBooleanFilterSelections(
	expression: LuceneExpression,
	options: { buttonLabels: string[]; apiNames: string[]; fixedValues?: string[]; filterId: string }
): BooleanSelectionGroups {
	const { buttonLabels, apiNames, fixedValues, filterId } = options;
	const fields = [...apiNames];
	if (fixedValues && filterId === 'bocRole') {
		fields.push(...buttonLabels.map(label => `${ apiNames[0] }.${ label }`));
	}
	const limit = fixedValues ? buttonLabels.length : apiNames.length;
	const [baseApiName] = apiNames;
	const normalizedFixed = fixedValues
		? fixedValues.map(value => value.toLowerCase())
		: [];
	const labelIndex = new Map<string, number>();
	let labelPosition = 0;
	for (const label of buttonLabels) {
		labelIndex.set(label, labelPosition);
		labelPosition += 1;
	}

	const createEmptyRow = (): BooleanSelectionRow => {
		const row: BooleanSelectionRow = [];
		for (let index = 0; index < limit; index++) {
			row.push({
				apiName: fixedValues ? baseApiName : apiNames[index],
				selected: false,
				value: fixedValues ? fixedValues[index] : undefined
			});
		}
		return row;
	};

	const conditions = extractConditionsByFields(expression, fields);
	const seen = new Set<string>();
	const values: BooleanSelectionGroups = [];
	let aggregatedRow: BooleanSelectionRow | undefined;
	for (const condition of conditions) {
		let row: BooleanSelectionRow;
		if (fixedValues) {
			if (!aggregatedRow) {
				aggregatedRow = createEmptyRow();
			}
			row = aggregatedRow;
		} else {
			row = createEmptyRow();
		}
		let touched = false;
		for (const [field, raw] of Object.entries(condition)) {
			let index = -1;
			if (fixedValues) {
				if (filterId === 'bocRole') {
					const label = field.slice(field.lastIndexOf('.') + 1);
					index = labelIndex.get(label) ?? -1;
				} else {
					const text = ensureString(raw);
					if (text) {
						const normalized = text.toLowerCase();
						index = normalizedFixed.indexOf(normalized);
					}
				}
			} else {
				index = apiNames.indexOf(field);
			}
			if (index < 0 || index >= row.length) {
				continue;
			}
			row[index].selected = true;
			touched = true;
		}
		if (!touched) {
			continue;
		}
		if (!fixedValues) {
			const key = row
				.map(item => `${ item.apiName }:${ item.selected ? '1' : '0' }:${ item.value ?? '' }`)
				.join('|');
			if (seen.has(key)) {
				continue;
			}
			seen.add(key);
			values.push(row);
		}
	}
	if (fixedValues && aggregatedRow) {
		const hasSelection = aggregatedRow.some(item => item.selected);
		if (hasSelection) {
			const key = aggregatedRow
				.map(item => `${ item.apiName }:${ item.selected ? '1' : '0' }:${ item.value ?? '' }`)
				.join('|');
			if (!seen.has(key)) {
				seen.add(key);
				values.push(aggregatedRow);
			}
		}
	}
	return values;
}

export function parseShipmentOrderNumbers(
	expression: LuceneExpression,
	field: string
): { reference?: string; item?: string }[] {
	const matches = collectFieldValues(expression, [field]).get(field) ?? [];
	const collected: { reference?: string; item?: string }[] = [];
	for (const raw of matches) {
		const text = ensureString(raw);
		if (!text) {
			continue;
		}
		const { reference: parsedReference, item } = parseOrderNumberValue(text);
		const reference = parsedReference ?? (item ? undefined : text.trim() || undefined);
		if (reference || item) {
			collected.push({ reference, item });
		}
	}
	return deduplicate(collected, entry => `${ entry.reference ?? '' }|${ entry.item ?? '' }`);
}

export function parseNumericRanges(
	expression: LuceneExpression,
	field: string
): { from: string; to: string }[] {
	const conditions = extractConditionsByFields(expression, [field]);
	const collected: { from: string; to: string }[] = [];
	for (const condition of conditions) {
		const range = ensureRangeValue(condition[field]);
		if (!range) {
			continue;
		}
		collected.push({ from: range.from, to: range.to });
	}
	return deduplicate(collected, value => `${ value.from }|${ value.to }`);
}

export function parseDocumentLastConnectionDateSelections(
	expression: LuceneExpression,
	field: string,
	fixedValue?: string
): DocumentLastConnectionSelectionRow[] {
	const conditions = extractConditionsByFields(expression, [field]);
	const seen = new Set<string>();
	const values: DocumentLastConnectionSelectionRow[] = [];
	for (const condition of conditions) {
		const range = ensureRangeValue(condition[field]);
		const value = range?.from ?? fixedValue ?? '';
		if (!value) {
			continue;
		}
		const key = `${ field }|${ value }`;
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		values.push([{ apiName: field, selected: true, value }]);
	}
	return values;
}

export function parseDocumentSuggestiveListIds(
	expression: LuceneExpression,
	baseField: string,
	entityFields: string[],
	entityName?: string
): DocumentSuggestiveListItem[] {
	const targets = [baseField, ...entityFields];
	const fieldValues = collectFieldValues(expression, targets);
	const records = new Map<string, DocumentSuggestiveListItem>();
	for (const [field, values] of fieldValues.entries()) {
		for (const raw of values) {
			const id = ensureString(raw);
			if (!id) {
				continue;
			}
			const key = id.toLowerCase();
			const current = records.get(key) ?? { id, code: undefined, entityName: undefined };
			if (field === baseField) {
				current.id = id;
			}
			if (entityName && entityFields.includes(field)) {
				current.entityName = entityName;
			}
			records.set(key, current);
		}
	}
	return [...records.values()];
}
